import type { TemplateSave } from '@morph-mapper/types';
import { reviveState, stringifyState } from '@morph-mapper/utils';

export const SELECTED_FILE_STORAGE_KEY = 'mm_selected_file';
export const UNSAVED_SAVE_STORAGE_KEY = 'mm_unsaved_save';

let latest = 0;

export const clearPersistedFile = () => {
  latest++;
  sessionStorage.removeItem(SELECTED_FILE_STORAGE_KEY);
};

export const persistFile = async (templateId: string, file: File) => {
  latest++;
  const id = latest;

  const dataUrl = await new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
  });
  if (id !== latest) return;

  try {
    sessionStorage.setItem(
      SELECTED_FILE_STORAGE_KEY,
      JSON.stringify({
        templateId,
        name: file.name,
        type: file.type,
        lastModified: file.lastModified,
        dataUrl,
      }),
    );
  } catch {
    clearPersistedFile();
  }
};

export const loadPersistedFile = (templateId: string) => {
  try {
    const saved = JSON.parse(
      sessionStorage.getItem(SELECTED_FILE_STORAGE_KEY) ?? 'null',
    );
    if (saved?.templateId !== templateId) return undefined;

    const binary = window.atob(saved.dataUrl.split(',')[1]);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return new File([bytes], saved.name, {
      type: saved.type,
      lastModified: saved.lastModified,
    });
  } catch {
    return undefined;
  }
};

export const clearPersistedSave = () => {
  sessionStorage.removeItem(UNSAVED_SAVE_STORAGE_KEY);
};

export const persistSave = (templateId: string, save: unknown) => {
  try {
    sessionStorage.setItem(
      UNSAVED_SAVE_STORAGE_KEY,
      stringifyState({
        templateId,
        save,
      }),
    );
  } catch {
    clearPersistedSave();
  }
};

export const loadPersistedSave = (templateId: string) => {
  try {
    const saved = reviveState(
      sessionStorage.getItem(UNSAVED_SAVE_STORAGE_KEY) ?? 'null',
    );
    if (saved?.templateId !== templateId) return undefined;

    return saved.save as TemplateSave | undefined;
  } catch {
    return undefined;
  }
};
